import { Link } from 'react-router-dom'
import { AlertTriangle, ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLicenseStore } from '../stores/licenseStore'
import { useAuthStore } from '../stores/authStore'
import { PERMS } from '../lib/permissions'
import { DEMO_MODE } from './DemoBanner'

/**
 * License-countdown strip. The gateway stamps responses with
 * `x-license-status` (captured into the license store by the apiClient
 * interceptor); file-key countdowns and SaaS org postures render here so
 * the state is visible on every page without polling a status endpoint.
 * The file-key expiry escalation is 30 (appear) / 7 (urgent) / 1
 * (critical countdown) days out — the warning ahead of the deny-all
 * terminal, per the 2026-10-20 licensing decision.
 *
 * Fixed bottom strip like DemoBanner (no theft of working-surface height);
 * when the demo strip is also up this sits one strip above it. Sysadmins get
 * a link to the file-key page on file-key postures; everyone else sees the
 * countdown text only. Terminal postures (expired/invalid) render too — by
 * the time requests 403, the banner is the explanation. SaaS postures are
 * text-only: the org-key page needs a health-system id the banner cannot
 * know (there is no org index route).
 */
/** Expiry countdown at or below this many days renders urgent (rose).
 *  Mirrors the gateway's 30-day appearance threshold: the banner's tiers
 *  are 30 (appear) / 7 (urgent) / 1 (critical countdown). */
export const EXPIRY_URGENT_DAYS = 7

export default function LicenseBanner() {
  const { t } = useTranslation('common')
  const status = useLicenseStore((s) => s.status)
  const countdown = useLicenseStore((s) => s.countdown)
  const hasPermission = useAuthStore((s) => s.hasPermission)

  if (status === null || status === 'valid' || status === 'saas-ok') return null

  const urgent =
    status === 'grace' ||
    status === 'expired' ||
    status === 'invalid' ||
    status === 'saas-unverifiable' ||
    (status === 'expiring' && (countdown ?? EXPIRY_URGENT_DAYS + 1) <= EXPIRY_URGENT_DAYS)
  const message =
    status === 'evaluation'
      ? t('license.evalBanner', { count: countdown ?? 0 })
      : status === 'grace'
        ? t('license.graceBanner', { count: countdown ?? 0 })
        : status === 'expiring'
          ? t('license.expiringBanner', { count: countdown ?? 0 })
          : status === 'expired'
            ? t('license.expiredBanner')
            : status === 'saas-unkeyed'
              ? t('license.saasUnkeyedBanner')
              : status === 'saas-unverifiable'
                ? t('license.saasUnverifiableBanner')
                : t('license.invalidBanner')

  const Icon = urgent ? ShieldAlert : AlertTriangle
  const canManage = hasPermission(PERMS.SYS_ADMIN_ALL)
  const isSaas = status === 'saas-unkeyed' || status === 'saas-unverifiable'

  return (
    <div
      role="alert"
      className={`fixed inset-x-0 z-40 flex items-center justify-center gap-2 border-t px-4 py-1 text-xs font-medium backdrop-blur-xs ${
        DEMO_MODE ? 'bottom-6' : 'bottom-0'
      } ${
        urgent
          ? 'border-rose-700/40 bg-rose-600/95 text-rose-50'
          : 'border-amber-600/40 bg-amber-500/95 text-amber-950'
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{message}</span>
      {canManage && !isSaas && (
        <Link
          to="/admin/licensing/file-key"
          className={`shrink-0 underline underline-offset-2 hover:opacity-80 ${
            urgent ? 'text-rose-50' : 'text-amber-950'
          }`}
        >
          {t('license.viewLicense')}
        </Link>
      )}
    </div>
  )
}
